import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { UserService } from './user.service';

export interface HealthDiagnosticData {
  v1: { title: string; value: number; label: string; factorySpec: number };
  v2: { title: string; value: number; label: string; factorySpec: number };
  v3: { title: string; value: number; label: string; factorySpec: number };
  a1: { title: string; value: number; label: string; factorySpec: number };
  a2: { title: string; value: number; label: string; factorySpec: number };
  a3: { title: string; value: number; label: string; factorySpec: number };
  nl: { title: string; value: number; label: string; factorySpec: number };
  fl: { title: string; value: number; label: string; factorySpec: number };
  board_t: { title: string; value: number; label: string };
  last_err: { title: string; value: string; label: string };
  date: { title: string; value: string; label: string };
}

export interface HealthChecks {
  controllerOutputsOk: boolean;
  buildingVoltageOk: boolean;
  wireSizeOk: boolean;
  allZonesEnergizing: boolean;
  wattageDrop: string;
  maxTempEstimate: string;
  nonEnergizedZones: string;
  lowPowerOutputs: string;
}

export interface HealthElement {
  name: string;
  isHeating: boolean;
  isEnergized: boolean;
}

export interface HealthDiagnosticsResponse {
  diagnosticData: HealthDiagnosticData;
  healthChecks: HealthChecks;
  elements: HealthElement[];
}

@Injectable({
  providedIn: 'root'
})
export class HealthDiagnosticsService {
  private baseUrl = environment.claimApi;

  constructor(
    private http: HttpClient,
    private userService: UserService
  ) {}

  getHealthDiagnostics(kilnId: string, mac: string): Observable<HealthDiagnosticsResponse> {
    const url = `${this.baseUrl}/kilns/${kilnId}/health?mac=${mac}`;
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-access-token': this.getAuthToken(),
      'x-app-name-token': 'kiln-link'
    });

    return this.http.get<HealthDiagnosticsResponse>(url, { headers });
  }

  getFakeHealthDiagnostics(kilnId: string, mac: string): Observable<HealthDiagnosticsResponse> {
    const fakeResponse: HealthDiagnosticsResponse = {
      diagnosticData: {
        v1: { title: 'Voltage 1', value: 240.5, label: 'V', factorySpec: 240 },
        v2: { title: 'Voltage 2', value: 239.8, label: 'V', factorySpec: 240 },
        v3: { title: 'Voltage 3', value: 241.2, label: 'V', factorySpec: 240 },
        a1: { title: 'Amperage 1', value: 10.5, label: 'A', factorySpec: 16 },
        a2: { title: 'Amperage 2', value: 10.5, label: 'A', factorySpec: 16 },
        a3: { title: 'Amperage 3', value: 10.5, label: 'A', factorySpec: 16 },
        nl: { title: 'Neutral Load', value: 0.2, label: 'A', factorySpec: 0.5 },
        fl: { title: 'Fault Load', value: 0.0, label: 'A', factorySpec: 0.1 },
        board_t: { title: 'Board Temperature', value: 45.2, label: '°C' },
        last_err: { title: 'Last Error', value: 'None', label: '' },
        date: { title: 'Diagnostic Date', value: new Date().toISOString(), label: '' }
      },
      healthChecks: {
        controllerOutputsOk: true,
        buildingVoltageOk: true,
        wireSizeOk: true,
        allZonesEnergizing: true,
        wattageDrop: 'Normal',
        maxTempEstimate: '2100°F',
        nonEnergizedZones: 'None',
        lowPowerOutputs: 'None'
      },
      elements: [
        { name: 'Element 1', isHeating: true, isEnergized: true },
        { name: 'Element 2', isHeating: true, isEnergized: true },
        { name: 'Element 3', isHeating: false, isEnergized: true },
        { name: 'Element 4', isHeating: true, isEnergized: true },
        { name: 'Element 5', isHeating: false, isEnergized: false },
        { name: 'Element 6', isHeating: true, isEnergized: true },
        { name: 'Element 7', isHeating: true, isEnergized: true },
        { name: 'Element 8', isHeating: false, isEnergized: true }
      ]
    };

    return new Observable(observer => {
      // Simulate network delay
      setTimeout(() => {
        observer.next(fakeResponse);
        observer.complete();
      }, 500);
    });
  }

  private getAuthToken(): string {
    const loginData = this.userService.getLoginData();
    return loginData ? loginData.token : '';
  }
} 